<template>
  <Head title="Profil Jurusan" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Profil Jurusan', href: route('profil-jurusan.index') },
          ]"
        />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <!-- Form Input (left) -->
          <div class="order-2 col-span-1 space-y-6 lg:order-1">
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-building-columns sm:mr-3"></i>
                  {{
                    isEditMode ? "Edit Profil Jurusan" : "Tambah Profil Jurusan"
                  }}
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <form class="space-y-4" @submit.prevent="submitForm">
                  <div>
                    <InputLabel for="tentang" value="Tentang" class="mb-2" />
                    <QuillEditor
                      v-model:content="form.tentang"
                      toolbar="essential"
                      contentType="html"
                      class="w-full bg-white border border-gray-300 rounded-lg"
                      :ref="quillTentangRef"
                    />
                    <InputError :message="form.errors.tentang" class="mt-2" />
                  </div>

                  <div>
                    <InputLabel for="visi" value="Visi" class="mb-2" />
                    <QuillEditor
                      v-model:content="form.visi"
                      toolbar="essential"
                      contentType="html"
                      class="w-full bg-white border border-gray-300 rounded-lg"
                      :ref="quillVisiRef"
                    />
                    <InputError :message="form.errors.visi" class="mt-2" />
                  </div>

                  <div>
                    <InputLabel for="misi" value="Misi" class="mb-2" />
                    <QuillEditor
                      v-model:content="form.misi"
                      toolbar="essential"
                      contentType="html"
                      class="w-full bg-white border border-gray-300 rounded-lg"
                      :ref="quillMisiRef"
                    />
                    <InputError :message="form.errors.misi" class="mt-2" />
                  </div>

                  <div>
                    <InputLabel
                      for="akreditasi"
                      value="Akreditasi"
                      class="mb-2"
                    />
                    <QuillEditor
                      v-model:content="form.akreditasi"
                      toolbar="essential"
                      contentType="html"
                      class="w-full bg-white border border-gray-300 rounded-lg"
                      :ref="quillAkreditasiRef"
                    />
                    <InputError
                      :message="form.errors.akreditasi"
                      class="mt-2"
                    />
                  </div>

                  <!-- Submit Button -->
                  <div class="pt-4">
                    <PrimaryBtn
                      :disabled="form.processing"
                      class="w-full px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-floppy-disk"></i>
                      {{
                        form.processing
                          ? "Menyimpan..."
                          : isEditMode
                          ? "Update"
                          : "Simpan"
                      }}
                    </PrimaryBtn>
                  </div>

                  <!-- Cancel Button (only show in edit mode) -->
                  <div v-if="isEditMode" class="pt-2">
                    <button
                      type="button"
                      @click="cancelEdit"
                      class="w-full px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded-lg shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-times"></i>
                      Batal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <!-- Table (right) -->
          <div class="order-1 col-span-1 lg:col-span-2 lg:order-2">
            <!-- Header Section -->
            <div class="mb-4 sm:mb-6">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Daftar Profil Jurusan
                  </h3>
                  <p class="mt-1 text-sm text-gray-600">
                    Kelola informasi profil jurusan
                  </p>
                </div>
                <div class="mt-2 text-sm text-gray-500 sm:mt-0">
                  Total: {{ profil_jurusans.length }} profil
                </div>
              </div>
            </div>

            <!-- Table -->
            <div
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[150px]"
                      >
                        Tentang
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[150px]"
                      >
                        Visi
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[150px]"
                      >
                        Misi
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[150px]"
                      >
                        Akreditasi
                      </th>
                      <th
                        scope="col"
                        class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[100px]"
                      >
                        <span class="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="(profil, index) in profil_jurusans"
                      :key="profil.id"
                      class="transition-colors duration-150 hover:bg-gray-50"
                    >
                      <td
                        class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 align-top whitespace-nowrap sm:pl-6"
                      >
                        {{ index + 1 }}
                      </td>
                      <td class="px-3 py-4 text-sm text-gray-900 align-top">
                        <div
                          class="max-w-xs overflow-hidden"
                          v-html="profil.tentang"
                        ></div>
                      </td>
                      <td class="px-3 py-4 text-sm text-gray-900 align-top">
                        <div
                          class="max-w-xs overflow-hidden"
                          v-html="profil.visi"
                        ></div>
                      </td>
                      <td class="px-3 py-4 text-sm text-gray-900 align-top">
                        <div
                          class="max-w-xs overflow-hidden"
                          v-html="profil.misi"
                        ></div>
                      </td>
                      <td class="px-3 py-4 text-sm text-gray-900 align-top">
                        <div
                          class="max-w-xs overflow-hidden"
                          v-html="profil.akreditasi"
                        ></div>
                      </td>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right align-top sm:pr-6"
                      >
                        <div
                          class="flex items-center justify-end space-x-1 sm:space-x-2"
                        >
                          <!-- Edit Button -->
                          <button
                            @click="editProfil(profil)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 hover:text-indigo-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Edit"
                          >
                            <i class="w-3 h-3 fa-solid fa-edit"></i>
                            <span class="hidden ml-1 sm:inline">Edit</span>
                          </button>
                          <!-- Delete Button -->
                          <button
                            @click="destroy(profil.id)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 hover:text-red-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Hapus"
                          >
                            <i class="w-3 h-3 fa-solid fa-trash"></i>
                            <span class="hidden ml-1 sm:inline">Hapus</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { Head, Link, router, useForm } from "@inertiajs/vue3";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import { QuillEditor } from "@vueup/vue-quill";
import { ref } from "vue";
import Swal from "sweetalert2";
const props = defineProps({ profil_jurusans: Array });

const isEditMode = ref(false);
const form = useForm({
  id: null,
  tentang: "",
  visi: "",
  misi: "",
  akreditasi: "",
});
const quillVisiRef = ref(null);
const quillMisiRef = ref(null);
const quillAkreditasiRef = ref(null);
const quillTentangRef = ref(null);

function submitForm() {
  if (form.id) {
    form.put(route("profil-jurusan.update", form.id), {
      onStart: () => {
        Swal.fire({
          title: "Menyimpan...",
          text: "Sedang memperbarui profil jurusan",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
      },
      onSuccess: () => {
        form.reset();
        form.id = null;
        isEditMode.value = false;
        // Kosongkan QuillEditor secara eksplisit
        form.tentang = "";
        form.visi = "";
        form.misi = "";
        form.akreditasi = "";
        resetQuillEditors();

        Swal.fire({
          title: "Berhasil!",
          text: "Profil jurusan berhasil diperbarui",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Error!",
          text: "Terjadi kesalahan saat memperbarui data",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
    });
  } else {
    form.post(route("profil-jurusan.store"), {
      onStart: () => {
        Swal.fire({
          title: "Menyimpan...",
          text: "Sedang menyimpan profil jurusan",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
      },
      onSuccess: () => {
        form.reset();
        form.visi = "";
        form.misi = "";
        form.akreditasi = "";
        form.tentang = "";
        resetQuillEditors();

        Swal.fire({
          title: "Berhasil!",
          text: "Profil jurusan berhasil disimpan",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Error!",
          text: "Terjadi kesalahan saat menyimpan data",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
    });
  }
}

function resetQuillEditors() {
  if (quillVisiRef.value) {
    quillVisiRef.value.setHTML && quillVisiRef.value.setHTML("");
    if (quillVisiRef.value.editor && quillVisiRef.value.editor.setContents) {
      quillVisiRef.value.editor.setContents([]);
    }
  }
  if (quillTentangRef.value) {
    quillTentangRef.value.setHTML && quillTentangRef.value.setHTML("");
    if (
      quillTentangRef.value.editor &&
      quillTentangRef.value.editor.setContents
    ) {
      quillTentangRef.value.editor.setContents([]);
    }
  }
  if (quillMisiRef.value) {
    quillMisiRef.value.setHTML && quillMisiRef.value.setHTML("");
    if (quillMisiRef.value.editor && quillMisiRef.value.editor.setContents) {
      quillMisiRef.value.editor.setContents([]);
    }
  }
  if (quillAkreditasiRef.value) {
    quillAkreditasiRef.value.setHTML && quillAkreditasiRef.value.setHTML("");
    if (
      quillAkreditasiRef.value.editor &&
      quillAkreditasiRef.value.editor.setContents
    ) {
      quillAkreditasiRef.value.editor.setContents([]);
    }
  }
}

function editProfil(profil) {
  isEditMode.value = true;
  form.id = profil.id;
  form.visi = profil.visi || "";
  form.misi = profil.misi || "";
  form.akreditasi = profil.akreditasi || "";
  form.tentang = profil.tentang || "";

  // Set content untuk setiap QuillEditor
  setTimeout(() => {
    if (quillTentangRef.value) {
      quillTentangRef.value.setHTML &&
        quillTentangRef.value.setHTML(form.tentang);
    }
    if (quillVisiRef.value) {
      quillVisiRef.value.setHTML && quillVisiRef.value.setHTML(form.visi);
    }
    if (quillMisiRef.value) {
      quillMisiRef.value.setHTML && quillMisiRef.value.setHTML(form.misi);
    }
    if (quillAkreditasiRef.value) {
      quillAkreditasiRef.value.setHTML &&
        quillAkreditasiRef.value.setHTML(form.akreditasi);
    }
  }, 100);
}

function cancelEdit() {
  isEditMode.value = false;
  form.reset();
  form.id = null;
  form.visi = "";
  form.misi = "";
  form.akreditasi = "";
  form.tentang = "";
  resetQuillEditors();
}

function destroy(id) {
  Swal.fire({
    title: "Apakah Anda yakin?",
    text: "Data yang dihapus tidak dapat dikembalikan!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, Hapus!",
    cancelButtonText: "Batal",
  }).then((result) => {
    if (result.isConfirmed) {
      router.delete(route("profil-jurusan.destroy", id), {
        onSuccess: () => {
          if (form.id === id) {
            cancelEdit();
          }
          Swal.fire({
            title: "Berhasil!",
            text: "Data berhasil dihapus.",
            icon: "success",
            confirmButtonText: "OK",
          });
        },
        onError: () => {
          Swal.fire({
            title: "Error!",
            text: "Terjadi kesalahan saat menghapus data.",
            icon: "error",
            confirmButtonText: "OK",
          });
        },
      });
    }
  });
}
</script>
